import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  take,
} from 'rxjs/operators';
import { InvoiceApi } from '@app/services/invoice-api';
import { CompanyApi } from '@app/services/company-api';
import { InvoiceInterface } from '@app/interface/invoice';
import { CompanyInterface } from '@app/interface/company';
import { httpQueryParams } from '@app/services/base-authenticated.service';
import {
  SearchEntry,
  SearchResult,
  SEARCH_ACTIONS,
  SEARCH_PAGES,
} from './search-catalog';

/** Numero massimo di fatture/clienti mostrati per sezione. */
const REMOTE_LIMIT = 6;

/**
 * Orchestratore della ricerca globale: combina il catalogo statico
 * (pagine/azioni, filtrate in-memory) con la ricerca server-side di
 * fatture e clienti.
 */
@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  // Cache per organizzazione della lista clienti: la ricerca clienti filtra
  // client-side (come company-autocomplete) per non dipendere da un parametro
  // di filtro server-side non garantito. La lista viene caricata una volta e
  // riusata per tutta la sessione della palette.
  private companiesCache = new Map<number, Observable<CompanyInterface[]>>();

  constructor(
    private router: Router,
    private invoiceApi: InvoiceApi,
    private companyApi: CompanyApi,
  ) {}

  /** Filtra pagine e azioni in-memory. Sincrono. */
  searchStatic(
    term: string,
    orgId: number | null,
  ): { pages: SearchResult[]; actions: SearchResult[] } {
    const t = term.trim().toLowerCase();
    return {
      pages: SEARCH_PAGES.filter((e) => this.matches(e, t)).map((e) =>
        this.entryToResult('PAGINE', e, orgId),
      ),
      actions: SEARCH_ACTIONS.filter((e) => this.matches(e, t)).map((e) =>
        this.entryToResult('AZIONI', e, orgId),
      ),
    };
  }

  /** Ricerca server-side di fatture e clienti, combinati con forkJoin. */
  searchRemote(
    term: string,
    orgId: number | null,
  ): Observable<{ invoices: SearchResult[]; companies: SearchResult[] }> {
    return forkJoin({
      invoices: this.searchInvoices(term, orgId),
      companies: this.searchCompanies(term, orgId),
    });
  }

  private searchInvoices(
    term: string,
    orgId: number | null,
  ): Observable<SearchResult[]> {
    const t = term.trim();
    if (!t) {
      return of([]);
    }

    const params: httpQueryParams = {
      is_deleted: false,
      is_draft: false,
      is_preventive: false,
      is_saved_for_preview: false,
      ordering: '-progressive_number',
      page_size: REMOTE_LIMIT,
    };

    // Termine numerico -> numero progressivo; altrimenti nome cliente.
    if (/^\d+$/.test(t)) {
      params['progressive_number'] = Number(t);
    } else {
      params['company__company_name__icontains'] = t;
    }

    return this.invoiceApi.getInvoiceList<InvoiceInterface>(orgId, params).pipe(
      map((resp) =>
        resp.results
          .slice(0, REMOTE_LIMIT)
          .map((inv) => this.invoiceToResult(inv, orgId)),
      ),
      catchError(() => of([])),
    );
  }

  private searchCompanies(
    term: string,
    orgId: number | null,
  ): Observable<SearchResult[]> {
    const t = term.trim().toLowerCase();
    if (!t || orgId == null) {
      return of([]);
    }

    return this.getAllCompaniesCached(orgId).pipe(
      map((companies) =>
        companies
          .filter((c) => (c.company_name || '').toLowerCase().includes(t))
          .slice(0, REMOTE_LIMIT)
          .map((c) => this.companyToResult(c, orgId)),
      ),
      catchError(() => of([])),
    );
  }

  private getAllCompaniesCached(orgId: number): Observable<CompanyInterface[]> {
    let cached = this.companiesCache.get(orgId);
    if (!cached) {
      cached = this.companyApi
        .getAllCompanies<CompanyInterface>(orgId, { is_deleted: false })
        .pipe(
          filter((r) => r.completed),
          take(1),
          map((r) => r.results),
          shareReplay(1),
        );
      this.companiesCache.set(orgId, cached);
    }
    return cached;
  }

  private matches(entry: SearchEntry, term: string): boolean {
    if (!term) {
      return true; // stato vuoto: mostra tutti i suggerimenti
    }
    if (entry.label.toLowerCase().includes(term)) {
      return true;
    }
    return entry.keywords.some((k) => k.toLowerCase().includes(term));
  }

  private entryToResult(
    section: 'PAGINE' | 'AZIONI',
    entry: SearchEntry,
    orgId: number | null,
  ): SearchResult {
    return {
      section,
      title: entry.label,
      icon: entry.icon,
      action: () => this.router.navigate([`/${orgId}/${entry.path}`]),
    };
  }

  private invoiceToResult(
    inv: InvoiceInterface,
    orgId: number | null,
  ): SearchResult {
    const subtitle = [inv.company?.company_name, this.formatAmount(inv.amount)]
      .filter(Boolean)
      .join(' · ');
    return {
      section: 'FATTURE',
      title: `Fattura #${inv.progressive_number}`,
      subtitle: subtitle || undefined,
      icon: 'receipt_long',
      action: () => this.router.navigate([`/${orgId}/invoice/${inv.id}`]),
    };
  }

  private companyToResult(
    c: CompanyInterface,
    orgId: number | null,
  ): SearchResult {
    return {
      section: 'CLIENTI',
      title: c.company_name,
      subtitle: c.iva_number || c.c_f || undefined,
      icon: 'business',
      action: () => this.router.navigate([`/${orgId}/company/${c.id}`]),
    };
  }

  private formatAmount(amount: number | null | undefined): string {
    if (amount == null || isNaN(Number(amount))) {
      return '';
    }
    return (
      Number(amount).toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + ' €'
    );
  }
}
